import { apiClient } from '../../shared/api/client';

export interface PosSaleFlowItem {
  productId: string;
  name: string;
  sku: string;
  quantity: number;
  unitValue: number;
  unitCost?: number;
}

export interface PosSaleFlowPayment {
  method: PosUiPaymentMethod;
  value: number;
}

export interface PosSaleFlowInput {
  terminalId: string;
  items: PosSaleFlowItem[];
  payments: PosSaleFlowPayment[];
  offlineOperationId?: string;
}

export interface PosSaleFlowResult {
  saleId: string;
}

/** Meios de pagamento que a tela do PDV oferece. */
export type PosUiPaymentMethod = 'CASH' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'PIX';

/**
 * Meio de pagamento da tela para o do dominio.
 *
 * `PosPaymentMethod` e MONEY | PIX | CREDIT_CARD | DEBIT_CARD | BOLETO |
 * BENEFIT | OTHER. Sao iguais menos o dinheiro, que no dominio e `MONEY`.
 *
 * O mapa anterior tinha `'CREDIT' -> 'OTHER'`: uma venda no credito seria
 * gravada como "Outro", e o resumo por meio de pagamento do fechamento de
 * caixa mostraria "Outros" em vez de cartao de credito. Fechando o mapa sobre
 * a uniao da tela, acrescentar um meio sem mapear quebra o typecheck em vez de
 * cair num `OTHER` silencioso.
 */
const METODO_DO_DOMINIO: Record<PosUiPaymentMethod, string> = {
  CASH: 'MONEY',
  CREDIT_CARD: 'CREDIT_CARD',
  DEBIT_CARD: 'DEBIT_CARD',
  PIX: 'PIX',
};

export function mapPaymentMethod(uiMethod: PosUiPaymentMethod): string {
  return METODO_DO_DOMINIO[uiMethod];
}

/**
 * Venda PDV online pelo fluxo stateful real (todo-review5 P4):
 * `POST /pos/sales` → `POST :id/items` (×N) → `POST :id/payments` (×N)
 * → `POST :id/complete {offlineOperationId?}`.
 * O `complete` é idempotente por venda (COMPLETED → ok).
 */
export async function submitPosSaleOnline(input: PosSaleFlowInput): Promise<PosSaleFlowResult> {
  if (input.items.length === 0) throw new Error('Adicione pelo menos um item');
  const total = input.items.reduce((sum, item) => sum + item.quantity * item.unitValue, 0);
  const paid = input.payments.reduce((sum, p) => sum + p.value, 0);
  if (paid < total) throw new Error('Valor pago inferior ao total da venda');

  const sale = await apiClient.post<{ id: string }>('/pos/sales', {
    terminalId: input.terminalId,
  });
  const saleId = sale.id;

  // Sem compensação automática: o cancelamento exige venda concluída +
  // justificativa, então uma falha no meio do fluxo deixa a venda aberta
  // no servidor (operador retoma pelo terminal). O erro original propaga.
  for (const item of input.items) {
    await apiClient.post(`/pos/sales/${saleId}/items`, {
      productId: item.productId,
      name: item.name,
      sku: item.sku,
      quantity: item.quantity,
      unitValue: item.unitValue,
      unitCost: item.unitCost ?? 0,
    });
  }
  for (const payment of input.payments) {
    await apiClient.post(`/pos/sales/${saleId}/payments`, {
      method: mapPaymentMethod(payment.method),
      value: payment.value,
    });
  }
  await apiClient.post(`/pos/sales/${saleId}/complete`, {
    ...(input.offlineOperationId ? { offlineOperationId: input.offlineOperationId } : {}),
  });

  return { saleId };
}

/**
 * Terminal da sessão de caixa aberta (`GET /pos/sessions` → status OPEN).
 * O backend exige sessão aberta por terminal para iniciar a venda.
 *
 * A rota devolve **envelope paginado** (`PaginatedCashSessionsDto`), não um
 * array: a versão anterior chamava `.find` direto na resposta e estourava
 * "sessions.find is not a function" — iniciar venda no PDV nunca funcionou.
 */
export async function getOpenSessionTerminalId(): Promise<string> {
  const resposta = await apiClient.get<{
    data: Array<{ terminalId: string; status: string }>;
  }>('/pos/sessions', { params: { status: 'OPEN', limit: '1' } });

  const sessoes = Array.isArray(resposta?.data) ? resposta.data : [];
  const aberta = sessoes.find((s) => s.status === 'OPEN');
  if (!aberta) throw new Error('Nenhuma sessão de caixa aberta. Abra uma sessão antes de vender.');
  return aberta.terminalId;
}
